import { z } from 'zod';
import { factSchema, stopKindSchema } from './artifact-parts.js';
import { boardArtifactSchema, calendarArtifactSchema, customersArtifactSchema, kpiArtifactSchema, moneyArtifactSchema, rankingArtifactSchema, } from './artifacts-loads.js';
import { chatArtifactSchema, driverArtifactSchema, vehicleArtifactSchema, } from './artifacts-people.js';
import { isoDateTime } from './common.js';
import { questionOptionSchema, recordKindSchema } from './domain.js';
/**
 * The panel beside an answer (core/domain/artifact.ts). Built by the server from
 * the records the answer used; the web only draws it. Asserted against the core
 * types in contract-types.test.ts.
 */
const stopSchema = z.object({
    kind: stopKindSchema,
    place: z.string(),
    /** The provider's appointment text, zone and all — not necessarily ISO. */
    at: z.string().optional(),
    /** `YYYY-MM-DD`, only when the provider's own text began with one. */
    day: z.string().optional(),
    facts: z.array(factSchema),
});
export const mapArtifactSchema = z.object({
    kind: z.literal('map'),
    label: z.string(),
    lat: z.number(),
    lon: z.number(),
    speedMph: z.number().optional(),
    place: z.string().optional(),
    at: z.string(),
});
export const hosArtifactSchema = z.object({
    kind: z.literal('hos'),
    driver: z.string(),
    status: z.string().optional(),
    clocks: z.array(z.object({
        label: z.string(),
        remainingMinutes: z.number().optional(),
        pct: z.number().optional(),
    })),
    at: z.string(),
});
export const loadArtifactSchema = z.object({
    kind: z.literal('load'),
    reference: z.string(),
    status: z.string().optional(),
    stops: z.array(stopSchema),
    facts: z.array(factSchema),
});
export const timelineArtifactSchema = z.object({
    kind: z.literal('timeline'),
    reference: z.string(),
    status: z.string().optional(),
    customer: z.string().optional(),
    events: z.array(stopSchema),
});
export const tableArtifactSchema = z.object({
    kind: z.literal('table'),
    recordKind: recordKindSchema,
    columns: z.array(z.string()),
    rows: z.array(z.array(z.string())),
    truncated: z.boolean(),
});
export const emailArtifactSchema = z.object({
    kind: z.literal('email'),
    messages: z.array(z.object({
        from: z.string().optional(),
        to: z.array(z.string()),
        subject: z.string(),
        preview: z.string().optional(),
        at: z.string(),
    })),
});
export const docArtifactSchema = z.object({
    kind: z.literal('doc'),
    title: z.string(),
    subtitle: z.string().optional(),
    // Defaulted, not required: a conversation stored before documents carried
    // their own fields has none, and an old message must still open.
    lines: z.array(factSchema).default([]),
    text: z.string(),
    truncated: z.boolean(),
    at: z.string(),
});
export const chartArtifactSchema = z.object({
    kind: z.literal('chart'),
    recordKind: recordKindSchema.optional(),
    points: z.array(z.object({ day: z.string(), count: z.number().int() })),
});
export * from './artifact-parts.js';
export * from './artifacts-loads.js';
export * from './artifacts-people.js';
export const artifactSchema = z.discriminatedUnion('kind', [
    mapArtifactSchema,
    hosArtifactSchema,
    loadArtifactSchema,
    timelineArtifactSchema,
    tableArtifactSchema,
    emailArtifactSchema,
    docArtifactSchema,
    chartArtifactSchema,
    moneyArtifactSchema,
    boardArtifactSchema,
    customersArtifactSchema,
    calendarArtifactSchema,
    rankingArtifactSchema,
    kpiArtifactSchema,
    chatArtifactSchema,
    vehicleArtifactSchema,
    driverArtifactSchema,
]);
/** One source the answer drew on. */
export const citationSchema = z.object({
    sourceKind: questionOptionSchema.optional(),
    label: z.string(),
    retrievedAt: isoDateTime,
    count: z.number().int(),
});
//# sourceMappingURL=artifacts.js.map